import type {
  SalonSchemeFloor,
  SaloonSchemeCell,
} from 'shared/api/beket/search'

import type { CellSchemaRender } from '../component/cells/module'

const FREE_SEAT_STATUS = 0

const hasSeatNumber = (seatNumber?: number | null) => {
  return typeof seatNumber === 'number' && seatNumber > 0
}

const isSeatBusyByStatus = (status?: number | string | null) => {
  if (status === null || status === undefined || status === '') return false

  const preparedStatus = Number(status)

  return Number.isFinite(preparedStatus) && preparedStatus !== FREE_SEAT_STATUS
}

export const prepareSaloonScheme = (
  data: SaloonSchemeCell[] = [],
): CellSchemaRender[] => {
  return data.map((item) => {
    const seat = hasSeatNumber(item.seat_number)
      ? item.seat_number || undefined
      : undefined

    return {
      ...item,
      seat,
      type: seat ? 'seat' : 'empty',
    }
  })
}

export const getSchemeRows = (data: CellSchemaRender[] = []) => {
  return Array.from(new Set(data.map((item) => item.row))).sort((a, b) => a - b)
}

export const getSchemeCols = (data: CellSchemaRender[] = []) => {
  return Array.from(new Set(data.map((item) => item.col))).sort((a, b) => a - b)
}

const getFirstSeatCell = (data: CellSchemaRender[] = []) => {
  return data
    .filter((item) => hasSeatNumber(item.seat))
    .sort((a, b) => (a.seat || 0) - (b.seat || 0))[0]
}

// Reverse the axis if the reference value sits closer to its last item,
// so the reference (seat 1) always ends up at the start of the axis.
const orientToStart = (values: number[], reference?: number) => {
  if (reference === undefined || values.length < 2) return values

  const distanceToStart = Math.abs(reference - values[0])
  const distanceToEnd = Math.abs(reference - values[values.length - 1])

  return distanceToEnd < distanceToStart ? [...values].reverse() : values
}

export type SchemeVerticalAxis = 'row' | 'col'

export interface SchemeLayout {
  verticalAxis: SchemeVerticalAxis
  verticalValues: number[]
  horizontalValues: number[]
}

export const getSchemeLayout = (
  data: CellSchemaRender[] = [],
): SchemeLayout => {
  const rows = getSchemeRows(data)
  const cols = getSchemeCols(data)
  const firstSeat = getFirstSeatCell(data)

  // Render the longer axis vertically so the saloon is laid out as a tall grid.
  const colsAreVertical = cols.length >= rows.length
  const verticalAxis: SchemeVerticalAxis = colsAreVertical ? 'col' : 'row'

  const verticalSource = colsAreVertical ? cols : rows
  const horizontalSource = colsAreVertical ? rows : cols

  // Put seat 1 in the top-left corner of the grid.
  const verticalValues = orientToStart(
    verticalSource,
    colsAreVertical ? firstSeat?.col : firstSeat?.row,
  )
  const horizontalValues = orientToStart(
    horizontalSource,
    colsAreVertical ? firstSeat?.row : firstSeat?.col,
  )

  return { verticalAxis, verticalValues, horizontalValues }
}

export interface SchemeFloorRender {
  floor: number
  layout: SchemeLayout
  cellByPosition: Map<string, CellSchemaRender>
}

// The saloon comes as a list of floors, each with its own scheme,
// so every floor is laid out as a separate grid.
export const prepareSaloonFloors = (
  data: SalonSchemeFloor[] = [],
): SchemeFloorRender[] => {
  return [...data]
    .sort((a, b) => a.floor - b.floor)
    .map((item) => {
      const cells = prepareSaloonScheme(item.scheme)

      const cellByPosition = new Map<string, CellSchemaRender>()

      cells.forEach((cell) => {
        cellByPosition.set(`${cell.row}:${cell.col}`, cell)
      })

      return {
        floor: item.floor,
        layout: getSchemeLayout(cells),
        cellByPosition,
      }
    })
    .filter((item) => item.cellByPosition.size > 0)
}

export const getBookedSeats =
  (bookedSeatNumbers: Set<number>) => (seat?: CellSchemaRender) =>
    Boolean(
      seat?.seat &&
      (bookedSeatNumbers.has(seat.seat) || isSeatBusyByStatus(seat.status)),
    )
